import { jsPDF } from 'jspdf';
import { savePDF } from './savePDF';

const PRIMARY   = [177, 18, 23];   // matches collegeConfig primaryColor #b11217
const LIGHT_BG  = [245, 245, 245];
const DARK_TEXT = [30, 30, 30];
const MID_TEXT  = [80, 80, 80];
const WHITE     = [255, 255, 255];
const BORDER    = [200, 200, 200];

const PAGE_W   = 210;
const PAGE_H   = 297;
const MARGIN   = 14;
const COL_W    = PAGE_W - MARGIN * 2;

const getBase64Image = (src) =>
  new Promise((resolve) => {
    if (!src) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null);
    img.src = src;
  });

// Returns { branch: string, batch: string, name: string, regNo: string, email: string, mobile: string, status: string }
const normaliseAdminStudent = (student) => {
  const branch = student.profile?.department || student.assignedCourses?.[0]?.courseName || 'Unassigned';
  const batch  = student.assignedCourses?.[0]?.batch || 'N/A';
  return {
    branch,
    batch,
    name:   `${student.profile?.firstName || ''} ${student.profile?.lastName || ''}`.trim() || student.email,
    regNo:  student.profile?.registrationNumber || '—',
    email:  student.email || '—',
    mobile: student.profile?.mobile || '—',
    status: student.isActive ? 'Active' : 'Inactive',
  };
};

const normaliseTeacherStudent = (student, courseName) => ({
  branch: courseName || 'Course',
  batch:  student.batch || 'N/A',
  name:   `${student.profile?.firstName || ''} ${student.profile?.lastName || ''}`.trim() || student.email,
  regNo:  student.profile?.registrationNumber || '—',
  email:  student.email || '—',
  mobile: student.profile?.mobile || '—',
  status: 'Active',
});

/**
 * Build a nested map: branchName → batchName → student[]
 */
const groupStudents = (rows) => {
  const map = {};
  rows.forEach((r) => {
    if (!map[r.branch]) map[r.branch] = {};
    if (!map[r.branch][r.batch]) map[r.branch][r.batch] = [];
    map[r.branch][r.batch].push(r);
  });
  // Sort names within each batch
  Object.values(map).forEach((batches) =>
    Object.values(batches).forEach((arr) => arr.sort((a, b) => a.name.localeCompare(b.name)))
  );
  return map;
};

// ─── Drawing helpers ────────────────────────────────────────

const drawHeader = async (doc, collegeName, logoBase64, reportTitle, dateStr) => {
  // Logo
  let logoX = MARGIN;
  if (logoBase64) {
    try {
      doc.addImage(logoBase64, 'PNG', MARGIN, 8, 22, 22);
    } catch (_) {}
    logoX = MARGIN + 26;
  }

  // College name
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...PRIMARY);
  doc.text(collegeName, logoX, 16);

  // Report title
  doc.setFontSize(10);
  doc.setTextColor(...MID_TEXT);
  doc.text(reportTitle, logoX, 22);

  // Date (right-aligned)
  doc.setFontSize(9);
  doc.setTextColor(...MID_TEXT);
  doc.text(`Generated: ${dateStr}`, PAGE_W - MARGIN, 16, { align: 'right' });

  // Divider
  doc.setDrawColor(...PRIMARY);
  doc.setLineWidth(0.6);
  doc.line(MARGIN, 33, PAGE_W - MARGIN, 33);
};

const checkPageBreak = (doc, y, needed = 10) => {
  if (y + needed > PAGE_H - 14) {
    doc.addPage();
    return 40;
  }
  return y;
};

const drawBranchHeader = (doc, branchName, totalCount, y) => {
  doc.setFillColor(...PRIMARY);
  doc.roundedRect(MARGIN, y, COL_W, 9, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...WHITE);
  doc.text(branchName, MARGIN + 4, y + 6.2);
  doc.text(`Total: ${totalCount}`, PAGE_W - MARGIN - 3, y + 6.2, { align: 'right' });
  return y + 12;
};

const drawBatchSubheader = (doc, batchLabel, count, y) => {
  doc.setFillColor(...LIGHT_BG);
  doc.rect(MARGIN, y, COL_W, 7, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(MARGIN, y, COL_W, 7, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...MID_TEXT);
  doc.text(`Batch ${batchLabel}`, MARGIN + 3, y + 4.8);
  doc.text(`${count} student${count !== 1 ? 's' : ''}`, PAGE_W - MARGIN - 3, y + 4.8, { align: 'right' });
  return y + 10;
};

// Columns: [label, x, width, align]
const COLS = [
  { label: 'S.No', x: MARGIN,      w: 11, align: 'right'  },
  { label: 'Name', x: MARGIN + 13, w: 48, align: 'left'   },
  { label: 'Reg No',   x: MARGIN + 63,  w: 34, align: 'left' },
  { label: 'Email',    x: MARGIN + 99,  w: 57, align: 'left' },
  { label: 'Mobile',   x: MARGIN + 158, w: 24, align: 'left' },
  { label: 'Status',   x: MARGIN + 183, w: 13, align: 'center' },
];

const drawTableHeader = (doc, y) => {
  doc.setFillColor(240, 240, 240);
  doc.rect(MARGIN, y, COL_W, 7, 'F');
  doc.setDrawColor(...BORDER);
  doc.rect(MARGIN, y, COL_W, 7, 'S');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(...DARK_TEXT);
  COLS.forEach(({ label, x, w, align }) => {
    doc.text(label, align === 'right' ? x + w - 1 : align === 'center' ? x + w / 2 : x + 1, y + 4.8, { align });
  });
  return y + 8;
};

const drawStudentRow = (doc, row, serial, y, shade) => {
  if (shade) {
    doc.setFillColor(252, 252, 252);
    doc.rect(MARGIN, y, COL_W, 6.5, 'F');
  }
  doc.setDrawColor(...BORDER);
  doc.setLineWidth(0.2);
  doc.line(MARGIN, y + 6.5, MARGIN + COL_W, y + 6.5);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  doc.setTextColor(...DARK_TEXT);

  const cells = [
    { text: String(serial), col: COLS[0] },
    { text: row.name,        col: COLS[1] },
    { text: row.regNo,       col: COLS[2] },
    { text: row.email,       col: COLS[3] },
    { text: row.mobile,      col: COLS[4] },
    { text: row.status,      col: COLS[5] },
  ];

  cells.forEach(({ text, col }) => {
    const maxLen = Math.floor(col.w / 1.8);
    const display = text.length > maxLen ? text.slice(0, maxLen - 1) + '…' : text;
    doc.text(display, col.align === 'right' ? col.x + col.w - 1 : col.align === 'center' ? col.x + col.w / 2 : col.x + 1, y + 4.4, { align: col.align });
  });
  return y + 6.5;
};

const drawGrandTotal = (doc, total, y) => {
  y += 6;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY);
  doc.text(`Grand Total: ${total} student${total !== 1 ? 's' : ''}`, MARGIN, y);
  return y + 8;
};

// ─── Public API ─────────────────────────────────────────────

/**
 * Generate and download the student directory PDF.
 *
 * @param {Object[]} students   Raw student objects from API
 * @param {Object}   options
 * @param {string}   options.collegeName
 * @param {string}   [options.logoSrc]
 * @param {string}   [options.mode]        'admin' (default) | 'teacher'
 * @param {string}   [options.courseName]  Required when mode='teacher'
 * @param {string}   [options.title]       Override report title
 */
export const generateStudentDirectoryPDF = async (students, options = {}) => {
  const {
    collegeName = 'College',
    logoSrc,
    mode = 'admin',
    courseName = '',
    title,
  } = options;

  const logoBase64 = await getBase64Image(logoSrc);
  const dateStr    = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' });
  const reportTitle = title || (mode === 'teacher' ? `Student Directory — ${courseName}` : 'Student Directory (All Branches)');

  const rows = mode === 'teacher'
    ? students.map((s) => normaliseTeacherStudent(s, courseName))
    : students.map((s) => normaliseAdminStudent(s));

  const grouped = groupStudents(rows);
  const grandTotal = rows.length;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  await drawHeader(doc, collegeName, logoBase64, reportTitle, dateStr);

  let y = 38;
  let globalSerial = 1;

  const branchNames = Object.keys(grouped).sort();

  // ── Summary box ─────────────────────────────────────────
  y = checkPageBreak(doc, y, 12);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MID_TEXT);
  const branchCount = branchNames.length;
  doc.text(
    `${grandTotal} student${grandTotal !== 1 ? 's' : ''} across ${branchCount} branch${branchCount !== 1 ? 'es' : ''}`,
    MARGIN, y
  );
  y += 8;

  for (const branch of branchNames) {
    const batches    = grouped[branch];
    const batchNames = Object.keys(batches).sort();
    const branchTotal = batchNames.reduce((s, b) => s + batches[b].length, 0);

    y = checkPageBreak(doc, y, 24);
    y = drawBranchHeader(doc, branch, branchTotal, y);

    // Batch summary line
    const summaryParts = batchNames.map((b) => `${b}: ${batches[b].length}`);
    doc.setFont('helvetica', 'italic');
    doc.setFontSize(8);
    doc.setTextColor(...MID_TEXT);
    const summaryText = summaryParts.join('  |  ');
    const maxSummaryLen = 120;
    const display = summaryText.length > maxSummaryLen ? summaryText.slice(0, maxSummaryLen) + '…' : summaryText;
    doc.text(display, MARGIN + 2, y);
    y += 7;

    for (const batch of batchNames) {
      const list = batches[batch];
      y = checkPageBreak(doc, y, 22);
      y = drawBatchSubheader(doc, batch, list.length, y);
      y = drawTableHeader(doc, y);

      list.forEach((student, idx) => {
        y = checkPageBreak(doc, y, 8);
        if (y === 40) {
          // Fresh page — redraw table header
          y = drawTableHeader(doc, y);
        }
        y = drawStudentRow(doc, student, globalSerial++, y, idx % 2 === 1);
      });

      y += 4;
    }

    y += 5;
  }

  drawGrandTotal(doc, grandTotal, y);

  const fileName = mode === 'teacher'
    ? `student-directory-${(courseName || 'course').replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.pdf`
    : `student-directory-all-branches-${Date.now()}.pdf`;

  await savePDF(doc, fileName);
};
